// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { useToast, Toast } from '../components/Toast'

const IcDoor = ({ color = 'currentColor' }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3.5" y="2.5" width="13" height="15" rx="1.5" stroke={color} strokeWidth="1.5"/>
    <rect x="3.5" y="2.5" width="8" height="15" rx="1.5" stroke={color} strokeWidth="1.5" fill="none"/>
    <circle cx="10.5" cy="10" r="1" fill={color}/>
  </svg>
)
const IcMotion = ({ color = 'currentColor' }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="5" r="2" stroke={color} strokeWidth="1.5"/>
    <path d="M7 9.5C7 8.1 8.3 7 10 7s3 1.1 3 2.5V13H7V9.5z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10 13v3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 16h4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M14.5 7.5c.8.7 1.5 1.8 1.5 3" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M5.5 7.5C4.7 8.2 4 9.3 4 10.5" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
)
const IcEdit = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M11 2.5l2.5 2.5-7.5 7.5H3.5V10L11 2.5z" stroke="#9ca3af" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
)
const IcTrash = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 4.5h10M6 4.5V3h4v1.5M5 4.5l.7 8h4.6l.7-8" stroke="#9ca3af" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const IcChevron = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
    <path d="M4 6l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

function formatMotionAgo(motionAt: number, t: any, lang: string) {
  if (!motionAt) return t('deviceDetail.pro.motion.never')
  const secs = Math.floor(Date.now() / 1000) - motionAt
  if (secs < 60) return t('deviceDetail.pro.motion.lessThanMinute')
  if (secs < 3600) return t('deviceDetail.pro.motion.minutesAgo', { n: Math.floor(secs / 60) })
  if (secs < 86400) return t('deviceDetail.pro.motion.hoursAgo', { n: Math.floor(secs / 3600) })
  return new Date(motionAt * 1000).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function SensorSlot({ sensor, lang, onRename, onDelete, onSensitivity, onRoomLabel, onSensorType, onCancelAlarm, events, isOwner }: any) {
  const { t } = useTranslation()
  const [editing, setEditing] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const [val, setVal] = React.useState(sensor.name)
  const [pendingSens, setPendingSens] = React.useState<number | null>(null)
  const [editingRoom, setEditingRoom] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)

  const save = () => {
    if (val.trim()) onRename(sensor.slot, val.trim())
    setEditing(false)
  }

  const isDoor  = sensor.type === 'door'
  const isPir   = sensor.type === 'pir'
  const isSmoke = sensor.type === 'smoke'
  const doorOpen    = sensor.open === true
  const smokeActive = isSmoke && sensor.smokeAlarm === true
  const motionAt = sensor.motionAt || 0
  const motionSecsAgo = motionAt > 0 ? Math.floor(Date.now() / 1000) - motionAt : null
  const pirRecent = motionSecsAgo !== null && motionSecsAgo < 120

  const typeLabel = isDoor ? t('deviceDetail.pro.sensor.doorType') : isSmoke ? 'Dymový senzor' : t('deviceDetail.pro.sensor.pirType')
  const statusColor = isDoor ? (doorOpen ? '#ef4444' : '#16a34a') : isSmoke ? (smokeActive ? '#ef4444' : '#16a34a') : (pirRecent ? '#d97706' : '#9ca3af')
  const statusText = isDoor
    ? (doorOpen ? t('deviceDetail.pro.sensor.open') : t('deviceDetail.pro.sensor.closed'))
    : isSmoke
      ? (smokeActive ? '🔥 ALARM!' : '✅ V poriadku')
      : formatMotionAgo(motionAt, t, lang)

  const iconColor = isDoor ? (doorOpen ? '#ef4444' : '#2D7A3A') : isSmoke ? (smokeActive ? '#ef4444' : '#2D7A3A') : (pirRecent ? '#d97706' : '#6b7280')
  const bg = isDoor ? (doorOpen ? '#fef2f2' : '#f0fdf4') : isSmoke ? (smokeActive ? '#fef2f2' : '#f0fdf4') : (pirRecent ? '#fffbeb' : '#f9fafb')
  const border = isDoor ? (doorOpen ? '#fca5a5' : '#bbf7d0') : isSmoke ? (smokeActive ? '#fca5a5' : '#bbf7d0') : (pirRecent ? '#fde68a' : '#e5e7eb')

  const motionDate = motionAt > 0
    ? new Date(motionAt * 1000).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : t('deviceDetail.pro.sensor.noMotion')

  // Firmware maps PIR slot index to room string: pir[0]=Chodba, pir[1]=Kuchyna, etc.
  // slot N (1-indexed) → pir[N-1] → room string
  // Door events use room = "Dvere" (or sensor.name if renamed)
  const SLOT_TO_FW_ROOM: Record<number, string> = { 1: 'Chodba', 2: 'Kuchyna', 3: 'Obyvacka', 4: 'Spalna' }
  const fwRoom = SLOT_TO_FW_ROOM[sensor.slot]

  const sensorEvents = (events || [])
    .filter((ev: any) => {
      if (isDoor) {
        if (ev.type === 'door_open' || ev.type === 'door_close') {
          return ev.room === 'Dvere' || ev.room === 'Door' || ev.room === sensor.name
        }
        if (ev.type === 'motion') return ev.room === 'Dvere' || ev.room === 'Door' || ev.room === sensor.name
        return false
      }
      if (ev.type !== 'motion') return false
      // Filter by firmware room string (slot-based) — this is what firmware actually sends
      if (fwRoom) return ev.room === fwRoom
      // Fallback if slot unknown: show all motion events
      return true
    })
    .slice(0, 10)

  return (
    <div style={{ backgroundColor: bg, borderRadius: 14, border: `1.5px solid ${border}`, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', color: iconColor }}>
          {isDoor ? <IcDoor color={iconColor} /> : isSmoke ? <span style={{ fontSize: 18 }}>🔥</span> : <IcMotion color={iconColor} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }} onClick={() => !editing && setExpanded(e => !e)} role="button">
          {editing ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input autoFocus value={val}
                onChange={e => setVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
                style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 7, padding: '4px 8px', fontSize: 13, fontFamily: 'inherit' }} />
              <button onClick={save} style={{ background: '#2D7A3A', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>OK</button>
              <button onClick={() => setEditing(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sensor.name}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>
                {typeLabel}
                <span style={{ marginLeft: 6, fontWeight: 600, color: statusColor }}>· {statusText}</span>
              </p>
            </>
          )}
        </div>
        {!editing && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0, alignItems: 'center' }}>
            <button onClick={() => { setEditing(true); setVal(sensor.name) }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px 6px', borderRadius: 6, display: 'flex', alignItems: 'center' }}><IcEdit /></button>
            {confirmDelete ? (
              <>
                <button onClick={() => { setConfirmDelete(false); onDelete(sensor.slot, sensor.name) }}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Zmazať</button>
                <button onClick={() => setConfirmDelete(false)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
              </>
            ) : (
              <button onClick={() => setConfirmDelete(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px 6px', borderRadius: 6, display: 'flex', alignItems: 'center' }}><IcTrash /></button>
            )}
            <button onClick={() => setExpanded(e => !e)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px 4px', borderRadius: 6, display: 'flex', alignItems: 'center' }}><IcChevron open={expanded} /></button>
          </div>
        )}
      </div>

      {expanded && !editing && (
        <div style={{ borderTop: `1px solid ${border}`, padding: '12px 14px 14px', backgroundColor: 'rgba(255,255,255,0.6)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('deviceDetail.pro.sensor.lastActivity')}
              </p>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.4 }}>{motionDate}</p>
            </div>
            {(isDoor || isSmoke) && (
              <div>
                <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {t('deviceDetail.pro.sensor.currentState')}
                </p>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: statusColor }}>{statusText}</p>
              </div>
            )}
            {sensor.battery > 0 && (
              <div>
                <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {t('deviceDetail.pro.sensor.battery')}
                </p>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: sensor.battery < 20 ? '#ef4444' : sensor.battery < 40 ? '#f59e0b' : '#2D7A3A' }}>
                  {sensor.battery}%
                </p>
              </div>
            )}
            {/* Room label */}
            <div style={{ gridColumn: 'span 2' }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('deviceDetail.pro.sensor.roomLabel')}
              </p>
              {editingRoom ? (
                <select
                  value={sensor.roomLabel || ''}
                  onChange={e => { onRoomLabel(sensor.slot, e.target.value); setEditingRoom(false) }}
                  onBlur={() => setEditingRoom(false)}
                  autoFocus
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 7, padding: '5px 8px', fontSize: 13, fontFamily: 'inherit', backgroundColor: '#fff' }}
                >
                  <option value="">{t('deviceDetail.pro.sensor.roomNone')}</option>
                  {/* value = firmvérový string, label = preložený text */}
                  {[
                    { fw: 'Chodba',   label: t('deviceDetail.pro.sensor.rooms.hall') },
                    { fw: 'Kuchyna',  label: t('deviceDetail.pro.sensor.rooms.kitchen') },
                    { fw: 'Obyvacka', label: t('deviceDetail.pro.sensor.rooms.living') },
                    { fw: 'Spalna',   label: t('deviceDetail.pro.sensor.rooms.bedroom') },
                    { fw: 'Kupelna',  label: t('deviceDetail.pro.sensor.rooms.bathroom') },
                    { fw: 'WC',       label: t('deviceDetail.pro.sensor.rooms.wc') },
                    { fw: 'Garaz',    label: t('deviceDetail.pro.sensor.rooms.garage') },
                    { fw: 'Zahrada',  label: t('deviceDetail.pro.sensor.rooms.garden') },
                    { fw: 'Vstup',    label: t('deviceDetail.pro.sensor.rooms.entrance') },
                  ].map(r => <option key={r.fw} value={r.fw}>{r.label}</option>)}
                </select>
              ) : (() => {
                const roomMap: Record<string, string> = {
                  'Chodba':   t('deviceDetail.pro.sensor.rooms.hall'),
                  'Kuchyna':  t('deviceDetail.pro.sensor.rooms.kitchen'),
                  'Obyvacka': t('deviceDetail.pro.sensor.rooms.living'),
                  'Spalna':   t('deviceDetail.pro.sensor.rooms.bedroom'),
                  'Kupelna':  t('deviceDetail.pro.sensor.rooms.bathroom'),
                  'WC':       t('deviceDetail.pro.sensor.rooms.wc'),
                  'Garaz':    t('deviceDetail.pro.sensor.rooms.garage'),
                  'Zahrada':  t('deviceDetail.pro.sensor.rooms.garden'),
                  'Vstup':    t('deviceDetail.pro.sensor.rooms.entrance'),
                }
                const displayLabel = sensor.roomLabel ? (roomMap[sensor.roomLabel] || sensor.roomLabel) : t('deviceDetail.pro.sensor.roomNone')
                return (
                  <p onClick={() => setEditingRoom(true)} style={{ margin: 0, fontSize: 12, color: sensor.roomLabel ? '#1a1a1a' : '#d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {displayLabel}
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>✎</span>
                  </p>
                )
              })()}
            </div>
          </div>

          {/* Typ senzora selector */}
          {isOwner && onSensorType && (
            <div style={{ gridColumn: 'span 2', marginTop: 10 }}>
              <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Typ senzora</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { type: 'pir',   label: 'PIR pohyb',       icon: '🏃' },
                  { type: 'door',  label: 'Dvere / okno',    icon: '🚪' },
                  { type: 'smoke', label: 'Dymový senzor',   icon: '🔥' },
                ].map(opt => (
                  <button key={opt.type} onClick={() => onSensorType(sensor.slot, opt.type)}
                    style={{
                      padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      border: sensor.type === opt.type ? '2px solid #4a6cf7' : '1.5px solid #e5e7eb',
                      background: sensor.type === opt.type ? '#f0f4ff' : '#fff',
                      color: sensor.type === opt.type ? '#4a6cf7' : '#6b7280', cursor: 'pointer',
                    }}>
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Smoke alarm panel + "Varím" button */}
          {isSmoke && smokeActive && (
            <div style={{ marginTop: 12, backgroundColor: '#fef2f2', borderRadius: 10, padding: '12px 14px', border: '1.5px solid #fca5a5' }}>
              <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#ef4444' }}>🔥 Dymový senzor — ALARM!</p>
              <button
                onClick={() => onCancelAlarm && onCancelAlarm(sensor.slot)}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', backgroundColor: '#f59e0b', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                🍳 Varím — je to v poriadku
              </button>
            </div>
          )}

          {sensorEvents.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {isDoor ? t('deviceDetail.pro.sensor.doorHistory') : isSmoke ? 'Historia alarmu' : t('deviceDetail.pro.sensor.motionHistory')}
              </p>
              {sensorEvents.map((ev: any, i: number) => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'center', padding: '5px 0', borderTop: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)', gap: 8 }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>
                    {ev.type === 'door_open' ? '🔓' : ev.type === 'door_close' ? '🔒' : '🏃'}
                  </span>
                  <span style={{ flex: 1, fontSize: 12, color: '#374151' }}>
                    {new Date(ev.createdAt).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
          {sensorEvents.length === 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#d1d5db' }}>
              {t('deviceDetail.pro.sensor.motionHistoryEmpty')}
            </p>
          )}

          {isPir && (
            <div style={{ marginTop: 14 }}>
              <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('deviceDetail.pro.sensor.pirSensitivity')}
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                {([1, 2, 3] as const).map(level => {
                  const labels = [
                    t('deviceDetail.pro.sensor.low'),
                    t('deviceDetail.pro.sensor.medium'),
                    t('deviceDetail.pro.sensor.high'),
                  ]
                  const currentSens = pendingSens ?? sensor.sensitivity ?? 0
                  const active = currentSens === level
                  return (
                    <button
                      key={level}
                      disabled={!isOwner}
                      onClick={() => {
                        if (!isOwner) return
                        setPendingSens(level)
                        onSensitivity(sensor.slot, level)
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 4px',
                        fontSize: 12,
                        fontWeight: active ? 700 : 500,
                        borderRadius: 8,
                        border: active ? '2px solid #2D7A3A' : '1.5px solid #e5e7eb',
                        background: active ? '#f0fdf4' : '#fff',
                        color: active ? '#2D7A3A' : '#6b7280',
                        cursor: isOwner ? 'pointer' : 'default',
                        opacity: isOwner ? 1 : 0.45,
                        transition: 'all 0.15s',
                      }}
                    >
                      {labels[level - 1]}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function EmptySlot({ index }: any) {
  const { t } = useTranslation()
  return (
    <div style={{
      backgroundColor: '#f9fafb', borderRadius: 14, padding: '12px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      border: '1.5px dashed #e5e7eb',
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 19,
        backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 18, flexShrink: 0, color: '#d1d5db',
      }}>＋</div>
      <p style={{ margin: 0, fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>
        {t('deviceDetail.pro.slot.empty', { n: index + 1 })}
      </p>
    </div>
  )
}

export default function DeviceDetailPro({ device: initialDevice }: { device: any }) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const [device, setDevice] = useState(initialDevice)
  const [pairing, setPairing] = useState(false)
  const [pairingCountdown, setPairingCountdown] = useState(0)
  const [cmdSending, setCmdSending] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [events, setEvents] = useState<any[]>([])
  // deletedIEEEs je len pre optimisticke UI v aktualnej session — neperzistujeme do localStorage
  const [deletedIEEEs, setDeletedIEEEs] = useState<string[]>(() => {
    // Nacitame len removedSensors zo servera (nie localStorage ktory sposoboval bugy)
    return initialDevice?.config?.removedSensors || []
  })

  const cfg = device?.config || {}
  const isZigbee = device?.deviceType === 'motion_v2_zigbee' || device?.deviceType === 'motion_v2_radar_zb'
  // Filtruj deletedIEEEs — ale ak senzor pride zo servera (cfg.sensors), vzdy ho ukaz
  const sensors: any[] = (cfg.sensors || []).filter((s: any) => !deletedIEEEs.includes(s.ieee))

  const pairedCount = sensors.length || (cfg.pairedSensors ?? 0)
  const pirCount = sensors.filter((s: any) => s.type === 'pir').length
  const trainingDay = Math.min(cfg.trainingDay ?? 0, 7)
  const possiblyOut = cfg.possiblyOut ?? false
  const avgOutingMin = cfg.avgOutingMin ?? 90
  const lastRoom = cfg.lastRoom ?? '—'
  const lastMotion = (() => {
    if (cfg.lastMotion) return cfg.lastMotion
    const ev = events.find((e: any) => e.type !== 'ok')
    if (!ev) return events[0] ? new Date(events[0].createdAt).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'
    return new Date(ev.createdAt).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  })()

  // Aktívny alarm — posledný event je warning bez následného ok
  const activeAlarm = (() => {
    if (!events.length) return null
    for (const ev of events) {
      if (ev.type === 'ok') return null
      if (ev.type === 'warning_1') return { level: 1, label: t('deviceDetail.pro.alarm.warning1'), color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' }
      if (ev.type === 'warning_2') return { level: 2, label: t('deviceDetail.pro.alarm.warning2'), color: '#ef4444', bg: '#fef2f2', border: '#fca5a5' }
      if (ev.type === 'warning_3') return { level: 3, label: t('deviceDetail.pro.alarm.warning3'), color: '#dc2626', bg: '#fef2f2', border: '#f87171' }
    }
    return null
  })()

  const isOwner = !device?.isShared

  // Monitoring — manuálny mód
  const [activeStart, setActiveStart] = useState(cfg.activeStart ?? 8)
  const [activeEnd, setActiveEnd] = useState(cfg.activeEnd ?? 22)
  const [inactivityMin, setInactivityMin] = useState(cfg.inactivityMin ?? 90)
  const [ledEnabled, setLedEnabled] = useState<boolean>(cfg.ledEnabled !== false)
  const [latestFwVersion, setLatestFwVersion] = useState<string | null>(null)
  const [smsCredits, setSmsCredits] = useState<number | null>(null)
  const [phones, setPhones] = useState<string[]>(() => {
    if (Array.isArray(cfg.phones) && cfg.phones.length > 0) return cfg.phones
    if (cfg.phone) return [String(cfg.phone)]
    return ['']
  })
  const [notifType, setNotifType] = useState<string>(cfg.notifType ?? 'both')
  const [hbPeriod, setHbPeriod] = useState<string>(cfg.hbPeriod ?? 'Vypnuté')
  const [hbHour, setHbHour] = useState<number>(cfg.hbHour ?? 12)
  const [w2Delay, setW2Delay] = useState<number>(cfg.w2DelayMin ?? 30)
  const [w3Delay, setW3Delay] = useState<number>(cfg.w3DelayMin ?? 30)
  const [shares, setShares] = useState<any[]>([])
  const [shareEmail, setShareEmail] = useState('')
  const [shareLoading, setShareLoading] = useState(false)
  const [shareError, setShareError] = useState('')
  const [shareSuccess, setShareSuccess] = useState('')
  const { toast, showToast } = useToast()
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  useEffect(() => {
    setActiveStart(cfg.activeStart ?? 8)
    setActiveEnd(cfg.activeEnd ?? 22)
    setInactivityMin(cfg.inactivityMin ?? 90)
  }, [cfg.activeStart, cfg.activeEnd, cfg.inactivityMin])
  // Clear pendingMode keď server potvrdí nový mód
  useEffect(() => {
    if (pendingMode && cfg.mode === pendingMode) setPendingMode(null)
  }, [cfg.mode, pendingMode])

  const reload = useCallback(async (manual = false) => {
    try {
      const [devRes, evRes] = await Promise.all([
        api.get(`/devices/${device.id}`),
        api.get(`/devices/${device.id}/events`)
      ])
      setDevice(devRes.data.device)
      setEvents(evRes.data.events || [])
      if (manual) showToast(t('deviceDetail.pro.refreshed'), 'success')
    } catch {
      if (manual) showToast(t('deviceDetail.pro.refreshFailed'), 'error')
    }
  }, [device?.id, lang])

  useEffect(() => {
    const iv = setInterval(reload, 5000)
    return () => clearInterval(iv)
  }, [reload])

  useEffect(() => {
    api.get('/sms/credits').then(r => setSmsCredits(r.data.credits)).catch(() => {})
    const fwType = (device?.deviceType === 'motion_v2_zigbee' || device?.deviceType === 'motion_v2_radar_zb') ? 'v2-zigbee' : 'v1-wifi'
    api.get(`/devices/firmware/${fwType}/info`).then(r => setLatestFwVersion(r.data.version)).catch(() => {})
  }, [])

  // Ak senzor pride zo servera, odstran ho z deletedIEEEs (bol re-paired)
  useEffect(() => {
    const serverIEEEs = (cfg.sensors || []).map((s: any) => s.ieee)
    if (serverIEEEs.some((ieee: string) => deletedIEEEs.includes(ieee))) {
      setDeletedIEEEs(prev => prev.filter(ieee => !serverIEEEs.includes(ieee)))
    }
  }, [cfg.sensors])

  const loadShares = useCallback(async () => {
    if (device?.isShared) return
    try {
      const res = await api.get(`/devices/${device.id}/shares`)
      setShares(res.data.shares)
    } catch {}
  }, [device?.id, device?.isShared])

  useEffect(() => { loadShares() }, [loadShares])

  // Sync UI language to device config so backend can send SMS in correct language
  useEffect(() => {
    if (device?.id && lang) {
      api.patch(`/devices/${device.id}/config`, { config: { lang } }).catch(() => {})
    }
  }, [device?.id, lang])

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault()
    setShareLoading(true); setShareError(''); setShareSuccess('')
    try {
      await api.post(`/devices/${device.id}/share`, { email: shareEmail })
      setShareSuccess(t('deviceDetail.pro.inviteSent'))
      setShareEmail('')
      loadShares()
    } catch (err: any) {
      setShareError(err.response?.data?.error || t('deviceDetail.pro.inviteError'))
    } finally { setShareLoading(false) }
  }

  const handleRemoveShare = async (shareId: string) => {
    if (!window.confirm(t('deviceDetail.pro.removeAccessConfirm'))) return
    try {
      await api.delete(`/devices/${device.id}/shares/${shareId}`)
      setShares((s: any[]) => s.filter((x: any) => x.id !== shareId))
      showToast(t('deviceDetail.pro.accessRemoved'), 'success')
    } catch {
      showToast(t('deviceDetail.pro.accessRemoveFailed'), 'error')
    }
  }

  const handleLedToggle = async (enabled: boolean) => {
    setLedEnabled(enabled)
    try {
      await api.patch(`/devices/${device.id}/config`, { config: { ...cfg, ledEnabled: enabled } })
      await sendCommand('set_led', enabled ? 'on' : 'off')
    } catch {
      setLedEnabled(!enabled)
      showToast(t('deviceDetail.pro.ledFailed'), 'error')
    }
  }

  const sendCommand = async (type: string, value?: string) => {
    setCmdSending(true)
    try {
      const res = await api.post(`/devices/${device.id}/commands`, { type, value: value ?? '' })
      // Feedback pre konkrétne príkazy
      if (type === 'test_sms') {
        const sent = res.data?.results?.filter((r: any) => r.sent).length ?? 0
        const total = res.data?.results?.length ?? 0
        if (sent === 0) showToast(t('deviceDetail.pro.smsNotSent'), 'error')
        else showToast(t('deviceDetail.pro.smsSent', { sent, total }), 'success')
      } else if (type === 'test_push') {
        const sent = res.data?.sent ?? 0
        if (sent === 0) showToast(t('deviceDetail.pro.noPush'), 'error')
        else showToast(t('deviceDetail.pro.pushSent'), 'success')
      } else if (type === 'factory_reset') {
        showToast(t('deviceDetail.pro.factoryResetSent'), 'info')
      } else if (type === 'reset_training') {
        showToast(t('deviceDetail.pro.trainingReset'), 'success')
      }
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error
      if (type === 'test_push' && serverMsg) {
        showToast(t('deviceDetail.pro.pushNotEnabled'), 'error')
      } else if (type === 'test_sms' && serverMsg) {
        showToast(serverMsg, 'error')
      } else {
        showToast(t('deviceDetail.pro.commandFailed'), 'error')
      }
    } finally { setCmdSending(false) }
  }

  const handleStartPairing = async () => {
    setPairing(true)
    setPairingCountdown(60)
    try {
      await api.post(`/devices/${device.id}/commands`, { type: 'start_pairing', value: '' })
      showToast(t('deviceDetail.pro.pairingStarted'), 'info')
    } catch {
      showToast(t('deviceDetail.pro.pairingFailed'), 'error')
      setPairing(false)
      return
    }
    const iv = setInterval(() => {
      setPairingCountdown(prev => {
        if (prev <= 1) { clearInterval(iv); setPairing(false); reload(); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  const handleRename = async () => {
    if (!newName.trim()) return
    try {
      const res = await api.patch(`/devices/${device.id}/name`, { name: newName.trim() })
      setDevice(res.data.device)
      setRenaming(false)
      showToast(t('deviceDetail.pro.nameSaved'), 'success')
    } catch {
      showToast(t('deviceDetail.pro.nameSaveFailed'), 'error')
    }
  }

  const handleRenameSensor = async (slot: number, name: string) => {
    try {
      await api.post(`/devices/${device.id}/commands`, { type: 'rename_sensor', value: `${slot}:${name}` })
      showToast(t('deviceDetail.pro.sensorRenamed'), 'success')
    } catch {
      showToast(t('deviceDetail.pro.sensorRenameFailed'), 'error')
    }
  }

  const handleSensitivity = async (slot: number, level: number) => {
    try {
      await api.post(`/devices/${device.id}/commands`, { type: 'set_pir_sensitivity', value: `${slot}:${level}` })
      showToast(t('deviceDetail.pro.sensitivitySet'), 'success')
    } catch {
      showToast(t('deviceDetail.pro.sensitivityFailed'), 'error')
    }
  }

  const handleRoomLabel = async (slot: number, roomLabel: string) => {
    const updatedSensors = (cfg.sensors || []).map((s: any) =>
      s.slot === slot ? { ...s, roomLabel } : s
    )
    try {
      await api.patch(`/devices/${device.id}/config`, { config: { sensors: updatedSensors } })
      setDevice((d: any) => ({ ...d, config: { ...d.config, sensors: updatedSensors } }))
    } catch {
      showToast(t('deviceDetail.pro.sensitivityFailed'), 'error')
    }
  }

  const handleSensorType = async (slot: number, type: string) => {
    const updatedSensors = (cfg.sensors || []).map((s: any) =>
      s.slot === slot ? { ...s, type } : s
    )
    try {
      await api.patch(`/devices/${device.id}/config`, { config: { sensors: updatedSensors } })
      setDevice((d: any) => ({ ...d, config: { ...d.config, sensors: updatedSensors } }))
      await api.post(`/devices/${device.id}/commands`, { type: 'set_sensor_type', value: JSON.stringify({ slot, stype: type }) })
    } catch {
      showToast(t('deviceDetail.pro.sensitivityFailed'), 'error')
    }
  }

  const handleCancelAlarm = async (slot: number) => {
    try {
      await api.post(`/devices/${device.id}/cancel-alarm`)
      const updatedSensors = (cfg.sensors || []).map((s: any) =>
        s.slot === slot ? { ...s, smokeAlarm: false } : s
      )
      setDevice((d: any) => ({ ...d, config: { ...d.config, sensors: updatedSensors } }))
    } catch {
      showToast('Nepodarilo sa zrušiť alarm', 'error')
    }
  }

  const handleDeleteSensor = async (slot: number, name: string) => {
    const sensorToDelete = (cfg.sensors || []).find((s: any) => s.slot === slot)
    if (sensorToDelete?.ieee) {
      const updated = [...deletedIEEEs, sensorToDelete.ieee]
      setDeletedIEEEs(updated)
      const serverRemoved = [...new Set([...(cfg.removedSensors || []), sensorToDelete.ieee])]
      api.patch(`/devices/${device.id}/config`, {
        config: { ...(device.config || {}), removedSensors: serverRemoved }
      }).catch(() => {})
    }
    try {
      await api.post(`/devices/${device.id}/commands`, { type: 'remove_sensor', value: String(slot) })
      showToast(t('deviceDetail.pro.sensorRemoved'), 'success')
    } catch {
      showToast(t('deviceDetail.pro.sensorRemoveFailed'), 'error')
    }
  }

  const handleFactoryReset = async () => {
    if (!window.confirm(t('deviceDetail.pro.factoryResetConfirm'))) return
    await sendCommand('factory_reset')
  }

  const handleDeleteDevice = async () => {
    if (!window.confirm(t('deviceDetail.pro.deleteDeviceConfirm', { name: device.name }))) return
    try {
      await api.delete(`/devices/${device.id}`)
      navigate('/dashboard')
    } catch {
      showToast(t('deviceDetail.pro.deleteDeviceFailed'), 'error')
    }
  }

  const s = { backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }

  return (
    <div style={s}>
      {/* Header */}
      <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', fontSize: 22, color: '#2D7A3A', cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
          {renaming ? (
            <div style={{ flex: 1, display: 'flex', gap: 8 }}>
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRename()}
                style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', fontSize: 15, fontFamily: 'inherit' }} />
              <button onClick={handleRename}
                style={{ background: '#2D7A3A', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {t('deviceDetail.pro.save')}
              </button>
              <button onClick={() => setRenaming(false)}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>{device.name}</span>
              {isZigbee && <span style={{ fontSize: 11, backgroundColor: '#f0fdf4', color: '#2D7A3A', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>PRO</span>}
              <button onClick={() => { setRenaming(true); setNewName(device.name) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11 2.5l2.5 2.5-7.5 7.5H3.5V10L11 2.5z" stroke="#9ca3af" strokeWidth="1.4" strokeLinejoin="round"/></svg>
              </button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: device.isOnline ? '#2D7A3A' : '#d1d5db' }} />
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{device.isOnline ? 'Online' : 'Offline'}</span>
            <button onClick={() => reload(true)} title="Refresh"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8A5 5 0 108 3a5 5 0 00-3.5 1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M3 2.5v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px 48px' }}>

        {/* Alarm banner */}
        {activeAlarm && (
          <div style={{ backgroundColor: activeAlarm.bg, border: `1.5px solid ${activeAlarm.border}`, borderRadius: 14, padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>{activeAlarm.level === 3 ? '🆘' : activeAlarm.level === 2 ? '🚨' : '⚠️'}</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: activeAlarm.color }}>{activeAlarm.label}</p>
                {lastMotion !== '—' && (
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                    {t('deviceDetail.pro.alarm.lastMotionInfo', { room: lastRoom !== '—' ? lastRoom : '', time: lastMotion })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.post(`/devices/${device.id}/cancel-alarm`)
                  await reload(true)
                } catch { showToast(t('deviceDetail.pro.alarm.cancelFailed'), 'error') }
              }}
              style={{ marginTop: 10, width: '100%', padding: '8px', borderRadius: 8, border: `1.5px solid ${activeAlarm.border}`, backgroundColor: '#fff', color: activeAlarm.color, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {t('deviceDetail.pro.alarm.cancel')}
            </button>
          </div>
        )}

        {/* Status karty */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {/* Possibly out — len Zigbee */}
          {isZigbee && <div style={{ backgroundColor: possiblyOut ? '#fffbeb' : '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: `1.5px solid ${possiblyOut ? '#fcd34d' : 'transparent'}` }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('deviceDetail.pro.status')}
            </p>
            <div style={{ color: possiblyOut ? '#d97706' : '#2D7A3A', marginBottom: 2 }}>
              {possiblyOut ? (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M7.5 10.5C7.5 9 9 8 11 8s3.5 1 3.5 2.5v3H7.5v-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M11 13.5v2.5m-2 2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M14.5 11l1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 11l8-7 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 9.5V18a1 1 0 001 1h10a1 1 0 001-1V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><rect x="8.5" y="13" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/></svg>
              )}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: possiblyOut ? '#d97706' : '#2D7A3A' }}>
              {possiblyOut ? t('deviceDetail.pro.likelyOutside') : t('deviceDetail.pro.atHome')}
            </p>
            {possiblyOut && (
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>
                {t('deviceDetail.pro.avgOuting', { min: avgOutingMin })}
              </p>
            )}
          </div>}

          {/* Posledný pohyb */}
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('deviceDetail.pro.lastMotion')}
            </p>
            <div style={{ color: '#6b7280', marginBottom: 2 }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5"/><path d="M11 7v4.5l3 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{lastMotion}</p>
            {isZigbee && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>{lastRoom}</p>}
          </div>

          {/* Tréning — len Zigbee */}
          {isZigbee && <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('deviceDetail.pro.training')}
            </p>
            <div style={{ color: trainingDay >= 7 ? '#2D7A3A' : '#6b7280', marginBottom: 2 }}>
              {trainingDay >= 7 ? (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5"/><path d="M7 11l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="15" width="3" height="4" rx="1" stroke="currentColor" strokeWidth="1.4"/><rect x="8" y="11" width="3" height="8" rx="1" stroke="currentColor" strokeWidth="1.4"/><rect x="13" y="7" width="3" height="12" rx="1" stroke="currentColor" strokeWidth="1.4"/><rect x="18" y="3" width="1" height="16" rx=".5" fill="currentColor" opacity=".2"/></svg>
              )}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
              {trainingDay >= 7
                ? t('deviceDetail.pro.trainingComplete')
                : t('deviceDetail.pro.trainingDays', { day: trainingDay })}
            </p>
            {trainingDay < 7 && (
              <div style={{ marginTop: 6, height: 4, backgroundColor: '#f0f0f0', borderRadius: 2 }}>
                <div style={{ height: 4, width: `${(trainingDay / 7) * 100}%`, backgroundColor: '#2D7A3A', borderRadius: 2, transition: 'width 0.3s' }} />
              </div>
            )}
          </div>}

          {/* Režim */}
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('deviceDetail.pro.mode')}
            </p>
            <div style={{ color: '#2D7A3A', marginBottom: 2 }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 3L4 6v5c0 4 2.8 7.5 7 8.5 4.2-1 7-4.5 7-8.5V6L11 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{cfg.mode ?? '—'}</p>
          </div>
        </div>

        {/* Senzory — len Zigbee */}
        {isZigbee && <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px 16px 8px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
              {t('deviceDetail.pro.sensors')} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({pairedCount}/4)</span>
            </p>
            {!pairing ? (
              <button onClick={handleStartPairing} disabled={cmdSending || pairedCount >= 4}
                style={{ fontSize: 12, fontWeight: 600, color: pairedCount >= 4 ? '#9ca3af' : '#2D7A3A', background: 'none', border: 'none', cursor: pairedCount >= 4 ? 'default' : 'pointer', padding: 0 }}>
                + {t('deviceDetail.pro.addSensor')}
              </button>
            ) : (
              <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                📡 {t('deviceDetail.pro.pairing', { s: pairingCountdown })}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {sensors.map((s: any) => (
              <SensorSlot
                key={s.slot}
                sensor={s}
                lang={lang}
                onRename={handleRenameSensor}
                onDelete={handleDeleteSensor}
                onSensitivity={handleSensitivity}
                onRoomLabel={handleRoomLabel}
                onSensorType={handleSensorType}
                onCancelAlarm={handleCancelAlarm}
                events={events}
                isOwner={isOwner}
              />
            ))}
            {Array.from({ length: Math.max(0, 4 - sensors.length) }, (_, i) => (
              <EmptySlot key={`empty-${i}`} index={sensors.length + i} />
            ))}
          </div>
        </div>}

        {/* Nastavenia */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
            {t('deviceDetail.pro.settings')}
          </p>

          {/* Telefón — viacero kontaktov */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
                {t('deviceDetail.pro.smsContacts')}
              </p>
              {smsCredits !== null && (
                <span
                  onClick={() => navigate('/sms-credits')}
                  style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                    backgroundColor: smsCredits > 10 ? '#f0fdf4' : smsCredits > 3 ? '#fff7ed' : '#fef2f2',
                    color: smsCredits > 10 ? '#16a34a' : smsCredits > 3 ? '#ea580c' : '#dc2626',
                  }}
                >
                  {smsCredits} SMS
                </span>
              )}
            </div>
            {phones.map((ph, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input
                  value={ph}
                  disabled={!isOwner}
                  onChange={e => {
                    if (!isOwner) return
                    const updated = phones.map((p, j) => j === i ? e.target.value : p)
                    setPhones(updated)
                  }}
                  onBlur={() => {
                    if (!isOwner) return
                    const clean = phones.filter(p => p.trim())
                    api.patch(`/devices/${device.id}/config`, { config: { phones: clean } })
                    if (i === 0 && phones[0]) sendCommand('set_phone', phones[0])
                  }}
                  placeholder={`+421900123456`}
                  style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', opacity: isOwner ? 1 : 0.45 }}
                />
                {phones.length > 1 && isOwner && (
                  <button
                    onClick={() => {
                      const updated = phones.filter((_, j) => j !== i)
                      setPhones(updated)
                      api.patch(`/devices/${device.id}/config`, { config: { phones: updated.filter(p => p.trim()) } })
                    }}
                    style={{ padding: '0 10px', border: '1px solid #fee2e2', borderRadius: 8, background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}
                  >×</button>
                )}
              </div>
            ))}
            {phones.length < 5 && isOwner && (
              <button
                onClick={() => setPhones([...phones, ''])}
                style={{ fontSize: 13, color: '#fff', background: '#2D7A3A', border: 'none', borderRadius: 8, cursor: 'pointer', padding: '7px 14px', fontWeight: 600, marginTop: 4 }}
              >+ {t('deviceDetail.pro.addContact')}</button>
            )}
          </div>

          {/* Typ notifikácií */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
              {t('deviceDetail.pro.notifType')}
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { val: 'sms',  label: 'SMS' },
                { val: 'push', label: 'Push' },
                { val: 'both', label: 'SMS + Push' },
              ].map(opt => (
                <button key={opt.val} onClick={() => {
                  setNotifType(opt.val)
                  api.patch(`/devices/${device.id}/config`, { config: { notifType: opt.val } })
                }} style={{ flex: 1, padding: '8px 4px', border: `1.5px solid ${notifType === opt.val ? '#2D7A3A' : '#e5e7eb'}`, borderRadius: 8, background: notifType === opt.val ? '#f0fdf4' : '#fff', color: notifType === opt.val ? '#2D7A3A' : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Periodická správa */}
          <div style={{ marginBottom: 4 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
              {t('deviceDetail.pro.periodicMessage')}
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[
                { val: 'Denne',    labelKey: 'deviceDetail.pro.hbDaily' },
                { val: 'Týždenne', labelKey: 'deviceDetail.pro.hbWeekly' },
                { val: 'Vypnuté',  labelKey: 'deviceDetail.pro.hbOff' },
              ].map(opt => (
                <button key={opt.val} onClick={() => {
                  setHbPeriod(opt.val)
                  api.patch(`/devices/${device.id}/config`, { config: { hbPeriod: opt.val } })
                }} style={{ flex: 1, padding: '8px 4px', border: `1.5px solid ${hbPeriod === opt.val ? '#2D7A3A' : '#e5e7eb'}`, borderRadius: 8, background: hbPeriod === opt.val ? '#f0fdf4' : '#fff', color: hbPeriod === opt.val ? '#2D7A3A' : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
            {hbPeriod !== 'Vypnuté' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{t('deviceDetail.pro.sendAt')}</span>
                <input type="number" min={0} max={23} value={hbHour}
                  onChange={e => setHbHour(Number(e.target.value))}
                  onBlur={() => api.patch(`/devices/${device.id}/config`, { config: { hbHour } })}
                  style={{ width: 54, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '6px 0', fontSize: 16, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700 }} />
                <span style={{ fontSize: 12, color: '#9ca3af' }}>:00</span>
              </div>
            )}
          </div>

        </div>

      {/* Monitoring */}
      <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Monitoring</p>
        {(() => {
          const espMode = pendingMode ?? cfg.mode ?? 'Vypnute'
          const activeTab = (espMode === 'Strazenie' || espMode === 'Ucenie') ? 'Adaptivne'
            : (espMode === 'Manualne' || espMode === 'Absencia pohybu') ? 'Pohoda'
            : espMode === 'Alarm pri pohybe' ? 'Strazenie'
            : 'Vypnute'
          const tabIcons: Record<string, JSX.Element> = {
            Vypnute: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6"/>
                <line x1="7" y1="7" x2="13" y2="13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="13" y1="7" x2="7" y2="13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            ),
            Pohoda: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="5.5" r="2.2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M6.5 10.5C6.5 8.8 8 7.5 10 7.5s3.5 1.3 3.5 3v2.5H6.5v-2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M8 16c0-1.1.9-2 2-2s2 .9 2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M10 13v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M7.5 14.5l.8-.8M12.5 14.5l-.8-.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            ),
            Strazenie: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2.5L3.5 5v5c0 3.6 2.8 6.7 6.5 7.5 3.7-.8 6.5-3.9 6.5-7.5V5L10 2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M7.5 10l1.8 1.8 3.2-3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ),
            Adaptivne: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 14l4-4 3 3 4-5 3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="15.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M14 5.5h-2M15.5 4v-1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            ),
          }
          const tabs = [
            { key: 'Vypnute',   fw: 'Vypnute',                                    label: t('deviceDetail.pro.modeOff'),       disabled: false },
            { key: 'Pohoda',    fw: isZigbee ? 'Manualne' : 'Absencia pohybu',    label: t('deviceDetail.pro.modeWellbeing'), disabled: false },
            { key: 'Strazenie', fw: 'Alarm pri pohybe',                            label: t('deviceDetail.pro.modeGuard'),     disabled: false },
            ...(isZigbee ? [{ key: 'Adaptivne', fw: 'Ucenie', label: t('deviceDetail.pro.modeAdaptive'), disabled: pirCount < 2 }] : []),
          ]
          return (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, opacity: isOwner ? 1 : 0.45, pointerEvents: isOwner ? 'auto' : 'none' }}>
                {tabs.map(tab => {
                  const isActive = activeTab === tab.key
                  return (
                  <button key={tab.key}
                    disabled={tab.disabled || !isOwner}
                    title={tab.disabled ? t('deviceDetail.pro.adaptiveNeedsTwoSensors') : undefined}
                    onClick={() => { if (!tab.disabled) { setPendingMode(tab.fw); sendCommand('set_mode', tab.fw); if (!isZigbee && tab.key === 'Pohoda') { sendCommand('set_timeout_1', String(inactivityMin)); sendCommand('set_start_h', String(activeStart)); sendCommand('set_end_h', String(activeEnd)); } api.patch(`/devices/${device.id}/config`, { config: { mode: tab.fw } }).then(r => setDevice(r.data.device)).catch(() => {}) } }}
                    style={{ flex: 1, padding: '10px 4px 8px', border: `1.5px solid ${isActive ? '#2D7A3A' : tab.disabled ? '#f0f0f0' : '#e5e7eb'}`, borderRadius: 12, backgroundColor: isActive ? '#2D7A3A' : tab.disabled ? '#f9fafb' : '#fff', color: isActive ? '#fff' : tab.disabled ? '#d1d5db' : '#6b7280', fontSize: 11, fontWeight: 600, cursor: tab.disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}>
                    {tabIcons[tab.key]}
                    {tab.label}
                  </button>
                  )
                })}
              </div>

              {activeTab === 'Pohoda' && (
                <div>
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{t('deviceDetail.pro.monitoredHours')}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
                    <input type="number" min={0} max={23} value={activeStart}
                      disabled={!isOwner}
                      onChange={e => setActiveStart(Number(e.target.value))}
                      onBlur={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_active_start' : 'set_start_h', String(activeStart)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, activeStart } }).catch(() => {}) }}
                      style={{ width: 54, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 0', fontSize: 18, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, opacity: isOwner ? 1 : 0.45 }} />
                    <span style={{ fontSize: 14, color: '#9ca3af' }}>:00 —</span>
                    <input type="number" min={0} max={23} value={activeEnd}
                      disabled={!isOwner}
                      onChange={e => setActiveEnd(Number(e.target.value))}
                      onBlur={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_active_end' : 'set_end_h', String(activeEnd)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, activeEnd } }).catch(() => {}) }}
                      style={{ width: 54, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 0', fontSize: 18, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, opacity: isOwner ? 1 : 0.45 }} />
                    <span style={{ fontSize: 14, color: '#9ca3af' }}>:00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{t('deviceDetail.pro.alertAfterInactivity')}</p>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{inactivityMin} min</span>
                  </div>
                  <input type="range" min={15} max={300} step={15} value={inactivityMin}
                    disabled={!isOwner}
                    onChange={e => setInactivityMin(Number(e.target.value))}
                    onMouseUp={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_inactivity_min' : 'set_timeout_1', String(inactivityMin)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, inactivityMin } }).catch(() => {}) }}
                    onTouchEnd={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_inactivity_min' : 'set_timeout_1', String(inactivityMin)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, inactivityMin } }).catch(() => {}) }}
                    style={{ width: '100%', accentColor: '#2D7A3A', cursor: isOwner ? 'pointer' : 'default', opacity: isOwner ? 1 : 0.45 }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#d1d5db', marginTop: 2, marginBottom: 14 }}>
                    <span>15 min</span><span>300 min</span>
                  </div>
                  <div style={{ padding: '10px 12px', backgroundColor: '#f0fdf4', borderRadius: 10, fontSize: 12, color: '#166534', lineHeight: 1.5, marginBottom: 10 }}>
                    {t('deviceDetail.pro.wellbeingInfo', { start: activeStart, end: activeEnd, min: inactivityMin })}
                  </div>
                  {/* Escalation timeline */}
                  <div style={{ backgroundColor: '#f8f9fa', borderRadius: 10, padding: '12px 14px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {t('deviceDetail.pro.escalation.title')}
                    </p>
                    {[
                      { color: '#f59e0b', bg: '#fffbeb', labelKey: 'deviceDetail.pro.escalation.w1', minVal: inactivityMin, editable: false },
                      { color: '#ef4444', bg: '#fef2f2', labelKey: 'deviceDetail.pro.escalation.w2', minVal: w2Delay, editable: isOwner,
                        onChange: (v: number) => { if (!isOwner) return; setW2Delay(v); api.patch(`/devices/${device.id}/config`, { config: { w2DelayMin: v } }) } },
                      { color: '#dc2626', bg: '#fef2f2', labelKey: 'deviceDetail.pro.escalation.w3', minVal: w3Delay, editable: isOwner,
                        onChange: (v: number) => { if (!isOwner) return; setW3Delay(v); api.patch(`/devices/${device.id}/config`, { config: { w3DelayMin: v } }) } },
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 2 ? 6 : 0 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: step.color, flexShrink: 0 }} />
                        {i > 0 && (
                          <div style={{ position: 'absolute', marginTop: -22, marginLeft: 4, width: 2, height: 22, backgroundColor: '#e5e7eb' }} />
                        )}
                        <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>
                          {t(step.labelKey)}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {step.editable ? (
                            <input
                              type="number" min={5} max={120} step={5}
                              value={step.minVal}
                              onChange={e => step.onChange?.(Number(e.target.value))}
                              style={{ width: 46, border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '3px 0', fontSize: 13, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, color: step.color, opacity: isOwner ? 1 : 0.45 }}
                            />
                          ) : (
                            <span style={{ fontSize: 13, fontWeight: 700, color: step.color, minWidth: 30, textAlign: 'right' }}>{step.minVal}</span>
                          )}
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'Strazenie' && (
                <div>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                    {t('deviceDetail.pro.guardDesc')}
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{t('deviceDetail.pro.activeHours')}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" min={0} max={23} value={activeStart}
                      disabled={!isOwner}
                      onChange={e => setActiveStart(Number(e.target.value))}
                      onBlur={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_active_start' : 'set_start_h', String(activeStart)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, activeStart } }).catch(() => {}) }}
                      style={{ width: 54, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 0', fontSize: 18, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, opacity: isOwner ? 1 : 0.45 }} />
                    <span style={{ fontSize: 14, color: '#9ca3af' }}>:00 —</span>
                    <input type="number" min={0} max={23} value={activeEnd}
                      disabled={!isOwner}
                      onChange={e => setActiveEnd(Number(e.target.value))}
                      onBlur={() => { if (!isOwner) return; sendCommand(isZigbee ? 'set_active_end' : 'set_end_h', String(activeEnd)); api.patch(`/devices/${device.id}/config`, { config: { ...cfg, activeEnd } }).catch(() => {}) }}
                      style={{ width: 54, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 0', fontSize: 18, fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, opacity: isOwner ? 1 : 0.45 }} />
                    <span style={{ fontSize: 14, color: '#9ca3af' }}>:00</span>
                    <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>
                      {activeStart === 0 && activeEnd === 0 ? '(24/7)' : ''}
                    </span>
                  </div>
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: '#d1d5db' }}>
                    {t('deviceDetail.pro.guard247')}
                  </p>
                </div>
              )}

              {activeTab === 'Adaptivne' && pirCount < 2 && (
                <div style={{ padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: 10 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                    {t('deviceDetail.pro.adaptiveNeedsTwoSensors')}
                  </p>
                </div>
              )}

              {activeTab === 'Adaptivne' && pirCount >= 2 && (
                <div>
                  {espMode === 'Strazenie' ? (
                    <div style={{ padding: '12px 14px', backgroundColor: '#f0fdf4', borderRadius: 10 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#166534' }}>{t('deviceDetail.pro.adaptiveActive')}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#166534' }}>{t('deviceDetail.pro.adaptiveActiveDesc')}</p>
                    </div>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                        {t('deviceDetail.pro.adaptiveDesc')}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{t('deviceDetail.pro.progress')}</p>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>{t('deviceDetail.pro.trainingDays', { day: trainingDay })}</span>
                      </div>
                      <div style={{ height: 8, backgroundColor: '#f0f0f0', borderRadius: 4, marginBottom: 6 }}>
                        <div style={{ height: 8, width: `${(trainingDay / 7) * 100}%`, backgroundColor: '#2D7A3A', borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                      <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{t('deviceDetail.pro.trainingRemaining', { n: 7 - trainingDay })}</p>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'Vypnute' && (
                <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>
                  {t('deviceDetail.pro.monitoringOff')}
                </p>
              )}
            </>
          )
        })()}
      </div>

        {/* LED toggle */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '4px 0', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="3.5" stroke="#6b7280" strokeWidth="1.4"/><path d="M9 2v1.5M9 14.5V16M2 9h1.5M14.5 9H16M4.1 4.1l1.05 1.05M12.85 12.85l1.05 1.05M4.1 13.9l1.05-1.05M12.85 5.15l1.05-1.05" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 14, color: '#1a1a1a' }}>{t('deviceDetail.pro.ledIndicator')}</span>
            </div>
            <button
              onClick={() => handleLedToggle(!ledEnabled)}
              style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: ledEnabled ? '#22c55e' : '#d1d5db', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
            >
              <span style={{ position: 'absolute', top: 3, left: ledEnabled ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
          </div>
        </div>

        {/* Akcie */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          {[
            { label: t('deviceDetail.pro.testSms'), cmd: 'test_sms', icon: (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3.5" width="14" height="10" rx="2" stroke="#6b7280" strokeWidth="1.4"/><path d="M6 3.5V2M12 3.5V2M5 7.5h8M5 10.5h5" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round"/></svg>
            )},
            { label: t('deviceDetail.pro.testPush'), cmd: 'test_push', icon: (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2a5 5 0 015 5v3l1.5 2H2.5L4 10V7a5 5 0 015-5z" stroke="#6b7280" strokeWidth="1.4" strokeLinejoin="round"/><path d="M7 14.5a2 2 0 004 0" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round"/></svg>
            )},
            { label: t('deviceDetail.pro.resetTraining'), cmd: 'reset_training', icon: (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3.5 9A5.5 5.5 0 109 3.5a5.5 5.5 0 00-3.9 1.6" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round"/><path d="M3 2.5v3h3" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            )},
          ].map((item, i, arr) => (
            <button key={item.cmd} onClick={() => sendCommand(item.cmd)} disabled={cmdSending}
              style={{ width: '100%', padding: '14px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: '#1a1a1a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, borderBottom: i < arr.length - 1 ? '1px solid #f5f5f5' : 'none', fontFamily: 'inherit' }}>
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>

        {/* Zdieľanie */}
        {!device?.isShared && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
              {t('deviceDetail.pro.sharedAccess')}
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#9ca3af' }}>
              {t('deviceDetail.pro.sharedAccessDesc')}
            </p>
            {shareSuccess && <p style={{ fontSize: 13, color: '#2D7A3A', margin: '0 0 10px', fontWeight: 600 }}>✅ {shareSuccess}</p>}
            {shareError && <p style={{ fontSize: 13, color: '#e74c3c', margin: '0 0 10px' }}>⚠️ {shareError}</p>}
            <form onSubmit={handleShare} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="email" required
                placeholder={t('deviceDetail.pro.inviteeEmail')}
                value={shareEmail}
                onChange={e => setShareEmail(e.target.value)}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
              />
              <button type="submit" disabled={shareLoading}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {shareLoading ? '...' : t('deviceDetail.pro.invite')}
              </button>
            </form>
            {shares.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shares.map((s: any) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: 8 }}>
                    <div>
                      <span style={{ fontSize: 13, color: '#1a1a1a' }}>{s.sharedWithEmail}</span>
                      <span style={{ fontSize: 11, color: s.accepted ? '#2D7A3A' : '#f59e0b', marginLeft: 8, fontWeight: 600 }}>
                        {s.accepted ? t('deviceDetail.pro.shareActive') : t('deviceDetail.pro.sharePending')}
                      </span>
                    </div>
                    <button onClick={() => handleRemoveShare(s.id)}
                      style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}>
                      {t('deviceDetail.pro.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {device?.isShared && (
          <div style={{ backgroundColor: '#eff6ff', borderRadius: 16, padding: '12px 20px', marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
              👥 {t('deviceDetail.pro.sharedWithYou')}
            </p>
          </div>
        )}

        {/* OTA Firmware update */}
        {!device.isShared && (() => {
          const currentFw = cfg.firmware || null
          const updateAvailable = latestFwVersion && currentFw && latestFwVersion !== currentFw
          return (
          <div style={{ backgroundColor: updateAvailable ? '#fffbeb' : '#fff', borderRadius: 16, padding: '14px 20px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: updateAvailable ? '1.5px solid #fde68a' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('deviceDetail.pro.firmware.title')}
              </p>
              {updateAvailable && (
                <span style={{ fontSize: 11, fontWeight: 700, backgroundColor: '#f59e0b', color: '#fff', borderRadius: 6, padding: '2px 7px' }}>
                  {t('deviceDetail.pro.firmware.available')}
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6b7280' }}>
              {t('deviceDetail.pro.firmware.current')}: <strong>{currentFw || '—'}</strong>
            </p>
            {latestFwVersion && (
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
                {t('deviceDetail.pro.firmware.latest')}: <strong style={{ color: updateAvailable ? '#d97706' : '#16a34a' }}>{latestFwVersion}</strong>
              </p>
            )}
            <button
              disabled={cmdSending || !updateAvailable}
              onClick={async () => {
                if (!window.confirm(t('deviceDetail.pro.firmware.confirm'))) return
                try {
                  const firmwareUrl = (device?.deviceType === 'motion_v2_zigbee' || device?.deviceType === 'motion_v2_radar_zb')
                    ? 'https://vigilantapp.duckdns.org/firmware/v2/latest.bin'
                    : 'https://vigilantapp.duckdns.org/devices/firmware/v1-wifi/latest.bin'
                  await api.post(`/devices/${device.id}/commands`, { type: 'firmware_update', value: firmwareUrl })
                  showToast(t('deviceDetail.pro.firmware.started'), 'success')
                } catch {
                  showToast(t('deviceDetail.pro.firmware.failed'), 'error')
                }
              }}
              style={{ padding: '9px 16px', borderRadius: 8, border: `1.5px solid ${updateAvailable ? '#2D7A3A' : '#d1d5db'}`, background: updateAvailable ? '#f0fdf4' : '#f9fafb', color: updateAvailable ? '#2D7A3A' : '#9ca3af', fontSize: 13, fontWeight: 600, cursor: updateAvailable ? 'pointer' : 'default', fontFamily: 'inherit' }}
            >
              {t('deviceDetail.pro.firmware.update')}
            </button>
          </div>
          )
        })()}

        {/* Odstrániť zdieľané zariadenie zo svojho profilu */}
        {!isOwner && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1.5px solid #e5e7eb' }}>
            <button
              onClick={async () => {
                if (!window.confirm(t('deviceDetail.pro.leaveDeviceConfirm', { name: device.name }))) return
                try {
                  await api.delete(`/devices/${device.id}/leave`)
                  window.location.href = '/'
                } catch { showToast(t('deviceDetail.pro.deleteDeviceFailed'), 'error') }
              }}
              style={{ width: '100%', padding: '14px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, fontFamily: 'inherit' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 13l4-4-4-4M15 9H7M7 3H4a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {t('deviceDetail.pro.leaveDevice')}
            </button>
          </div>
        )}

        {/* Nebezpečná zóna — len pre vlastníka */}
        {isOwner && <div style={{ backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1.5px solid #fee2e2' }}>
          <p style={{ margin: 0, padding: '12px 20px 0', fontSize: 11, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('deviceDetail.pro.dangerZone')}
          </p>
          <button
            onClick={async () => {
              if (!window.confirm(t('deviceDetail.pro.resetPhonesConfirm'))) return
              try {
                await api.post(`/devices/${device.id}/reset-phones`)
                setPhones([''])
                showToast(t('deviceDetail.pro.resetPhonesDone'), 'success')
              } catch { showToast(t('deviceDetail.pro.sensitivityFailed'), 'error') }
            }}
            style={{ width: '100%', padding: '12px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #fee2e2', fontFamily: 'inherit' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3.5" width="14" height="10" rx="2" stroke="#dc2626" strokeWidth="1.4"/><path d="M6 3.5V2M12 3.5V2M5 7.5h3M5 10.5h2" stroke="#dc2626" strokeWidth="1.3" strokeLinecap="round"/><path d="M12 8l2 2m0-2l-2 2" stroke="#dc2626" strokeWidth="1.3" strokeLinecap="round"/></svg>
            {t('deviceDetail.pro.resetPhones')}
          </button>
          <button onClick={handleFactoryReset} disabled={cmdSending}
            style={{ width: '100%', padding: '12px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #fee2e2', fontFamily: 'inherit' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3.5 9A5.5 5.5 0 109 3.5a5.5 5.5 0 00-3.9 1.6" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round"/><path d="M3 2.5v3h3" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('deviceDetail.pro.factoryReset')}
          </button>
          <button onClick={handleDeleteDevice}
            style={{ width: '100%', padding: '12px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, fontFamily: 'inherit' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3.5 4.5h11M7 4.5V3h4v1.5M6 4.5l.6 10h4.8l.6-10" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('deviceDetail.pro.deleteDevice')}
          </button>
        </div>}

        {/* História udalostí */}
        {events.length > 0 && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
              {t('deviceDetail.pro.recentEvents')}
            </p>
            {events.slice(0, 6).map((ev: any) => (
              <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: 13, color: '#374151' }}>{ev.type}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {new Date(ev.createdAt).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Toast toast={toast} />
    </div>
  )
}
